import { z } from '@botpress/sdk'
import { AgentProject } from '../agent-project/agent-project.js'
import { ADK_VERSION, formatCode } from './utils.js'
import { createFile } from '../utils/fs.js'
import path from 'path'

export interface TableTypeInfo {
  name: string
  columns: Record<string, { type: string; computed?: boolean; searchable?: boolean }>
}

export async function generateTableTypes(project: AgentProject): Promise<void> {
  const tables: TableTypeInfo[] = []

  // Load each table file to get the actual column definitions
  for (const tableRef of project.tables) {
    try {
      const tablePath = path.join(project.path, tableRef.path)
      // Bust module cache to ensure fresh table on type generation
      const tableModule = await import(`${tablePath}?t=${Date.now()}`)
      const tableInstance = tableModule.default || tableModule[tableRef.export]

      if (tableInstance && tableInstance.columns) {
        const columns: Record<string, { type: string; computed?: boolean; searchable?: boolean }> = {}

        for (const [colName, colDef] of Object.entries(tableInstance.columns)) {
          let schema: z.ZodTypeAny
          let computed = false
          let searchable = false

          if (typeof colDef === 'object' && colDef !== null && 'schema' in colDef) {
            // Long form
            schema = (colDef as any).schema
            computed = (colDef as any).computed || false
            searchable = (colDef as any).searchable || false
          } else {
            // Short form
            schema = colDef as z.ZodTypeAny
          }

          // Convert Zod schema to TypeScript type
          const tsType = schema.toTypescriptType ? schema.toTypescriptType() : 'any' // Fallback if toTypescriptType is not available

          columns[colName] = { type: tsType, computed, searchable }
        }

        tables.push({
          name: tableRef.definition.name,
          columns,
        })
      }
    } catch (error) {
      console.warn(`Failed to load table ${tableRef.definition.name}:`, error)
    }
  }

  // Generate the type definitions
  let content = `
////////////////////////////////////////////////////////
// DO NOT EDIT THIS FILE DIRECTLY
// This file is auto-generated from the Botpress ADK
// ADK Version: ${ADK_VERSION}
// Generated at: ${new Date().toISOString()}
////////////////////////////////////////////////////////

declare module "@botpress/runtime/_types/tables" {
  export type TableDefinitions = {
`

  if (tables.length === 0) {
    content += '    [key: string]: {\n'
    content += '      Input: any;\n'
    content += '      Output: {\n'
    content += '        id: number;\n'
    content += '        createdAt: string;\n'
    content += '        updatedAt: string;\n'
    content += '      } & any;\n'
    content += '    };\n'
  } else {
    for (const table of tables) {
      // Generate the input type without computed columns
      const inputColumns = Object.entries(table.columns)
        .filter(([_, col]) => !col.computed)
        .map(([name, col]) => `        ${name}: ${col.type};`)
        .join('\n')

      // Generate the output type with all columns plus system fields
      const outputColumns = [
        '        id: number;',
        '        createdAt: string;',
        '        updatedAt: string;',
        ...Object.entries(table.columns).map(([name, col]) => `        ${name}: ${col.type};`),
      ].join('\n')

      content += `    // Table: ${table.name}\n`
      content += `    ${table.name}: {\n`
      content += `      Input: {\n${inputColumns}\n      };\n`
      content += `      Output: {\n${outputColumns}\n      };\n`
      content += `    };\n`
    }
  }

  content += `  };
}
`

  // Write to table-types.d.ts in the .adk folder
  const tableTypesPath = path.join(project.path, '.adk', 'table-types.d.ts')
  await createFile(tableTypesPath, await formatCode(content))
}
